import { useState, useEffect, useRef, useCallback } from 'react';
import Modal from './ui/Modal';
import BACKEND_URL from '../config';
import Cookies from 'js-cookie';
import { IconX, IconSearch, IconArrowLeft } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import { Document, Event } from '../types';

// Search results may not have documentation_retrieved_id but will have documentation_id
interface SearchDocument extends Omit<Document, 'documentation_retrieved_id'> {
  documentation_retrieved_id?: number;
  documentation_id?: number;
}

interface HumanOperatorMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentationSearchEventId: number;
  simulationToken: string;
  onEventCreated: (event: Event) => void;
}

/**
 * Split text into word-level tokens with their character positions.
 * Each token tracks its start/end index in the original text.
 */
interface TextToken {
  text: string;
  start: number;
  end: number;
  isWhitespace: boolean;
}

function tokenize(text: string): TextToken[] {
  const tokens: TextToken[] = [];
  // Split into word and whitespace tokens
  const regex = /(\S+|\s+)/g;
  let m: RegExpExecArray | null;
  while ((m = regex.exec(text)) !== null) {
    tokens.push({
      text: m[0],
      start: m.index,
      end: m.index + m[0].length,
      isWhitespace: /^\s+$/.test(m[0]),
    });
  }
  return tokens;
}

const HumanOperatorMessageModal = ({
  isOpen,
  onClose,
  documentationSearchEventId,
  simulationToken,
  onEventCreated,
}: HumanOperatorMessageModalProps) => {
  // Form state
  const [message, setMessage] = useState('');

  // Documents state — default docs from the documentation search
  const [defaultDocuments, setDefaultDocuments] = useState<SearchDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  // Search state — global pgvector search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchDocument[]>([]);
  const [searchingDocs, setSearchingDocs] = useState(false);
  const [showingSearchResults, setShowingSearchResults] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Selected doc
  const [selectedDocIndex, setSelectedDocIndex] = useState<number | null>(null);

  // Document text state
  const [documentText, setDocumentText] = useState('');
  const [loadingText, setLoadingText] = useState(false);

  // Quote selection state — click start/end on tokens
  const [quoteStartIdx, setQuoteStartIdx] = useState<number | null>(null);
  const [quoteEndIdx, setQuoteEndIdx] = useState<number | null>(null);
  const [selectedQuote, setSelectedQuote] = useState('');

  // In-document text search state
  const [textSearch, setTextSearch] = useState('');
  const [textSearchMatchIdx, setTextSearchMatchIdx] = useState<number>(-1);
  const highlightRef = useRef<HTMLSpanElement>(null);

  // Submit state
  const [submitting, setSubmitting] = useState(false);

  // The active document list (either default or search results)
  const activeDocuments: SearchDocument[] = showingSearchResults ? searchResults : defaultDocuments;

  // Fetch default documents from the documentation search when modal opens
  useEffect(() => {
    if (!isOpen || !documentationSearchEventId) return;
    setLoadingDocs(true);
    fetch(
      `${BACKEND_URL}/vnog/get_documents_from_documentation_search/${documentationSearchEventId}/`,
      { method: 'GET', credentials: 'include' }
    )
      .then((res) => res.json())
      .then((data) => {
        setDefaultDocuments(data || []);
        setLoadingDocs(false);
      })
      .catch(() => {
        toast.error('Failed to load documents');
        setLoadingDocs(false);
      });
  }, [isOpen, documentationSearchEventId]);

  // Debounced pgvector search when user types in the document search bar
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowingSearchResults(false);
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearchingDocs(true);
      setShowingSearchResults(true);
      fetch(
        `${BACKEND_URL}/vnog/search_documents/?query=${encodeURIComponent(searchQuery.trim())}`,
        { method: 'GET', credentials: 'include' }
      )
        .then((res) => res.json())
        .then((data) => {
          setSearchResults(data || []);
          setSearchingDocs(false);
          resetQuoteState();
        })
        .catch(() => {
          toast.error('Failed to search documents');
          setSearchingDocs(false);
        });
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Fetch document text when a document is selected
  useEffect(() => {
    if (selectedDocIndex === null || !activeDocuments[selectedDocIndex]) return;
    const doc = activeDocuments[selectedDocIndex];
    const docId = doc.document?.id || doc.document?.document_id;
    if (!docId) return;

    setLoadingText(true);
    setDocumentText('');
    resetQuoteState();

    fetch(`${BACKEND_URL}/vnog/get_document_text/${docId}/`, {
      method: 'GET',
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        setDocumentText(data.text || '');
        setLoadingText(false);
      })
      .catch(() => {
        toast.error('Failed to load document text');
        setLoadingText(false);
      });
  }, [selectedDocIndex, activeDocuments]);

  // Find text search match position (simple indexOf)
  useEffect(() => {
    if (!textSearch.trim() || !documentText) {
      setTextSearchMatchIdx(-1);
      return;
    }
    const idx = documentText.toLowerCase().indexOf(textSearch.toLowerCase());
    setTextSearchMatchIdx(idx);
  }, [textSearch, documentText]);

  // Scroll to search match or to quote highlight
  useEffect(() => {
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [textSearchMatchIdx, quoteStartIdx, quoteEndIdx]);

  // Compute the selected quote text whenever start/end change
  useEffect(() => {
    if (quoteStartIdx !== null && quoteEndIdx !== null && documentText) {
      const start = Math.min(quoteStartIdx, quoteEndIdx);
      const end = Math.max(quoteStartIdx, quoteEndIdx);
      setSelectedQuote(documentText.substring(start, end));
    } else {
      setSelectedQuote('');
    }
  }, [quoteStartIdx, quoteEndIdx, documentText]);

  const resetQuoteState = useCallback(() => {
    setQuoteStartIdx(null);
    setQuoteEndIdx(null);
    setSelectedQuote('');
    setTextSearch('');
    setTextSearchMatchIdx(-1);
  }, []);

  // Handle clicking on a token in the document text
  const handleTokenClick = useCallback((charStart: number, charEnd: number) => {
    if (quoteStartIdx === null) {
      // First click: set start
      setQuoteStartIdx(charStart);
      setQuoteEndIdx(null);
      setSelectedQuote('');
    } else if (quoteEndIdx === null) {
      // Second click: set end
      setQuoteEndIdx(charEnd);
    } else {
      // Third click: reset and start over
      setQuoteStartIdx(charStart);
      setQuoteEndIdx(null);
      setSelectedQuote('');
    }
  }, [quoteStartIdx, quoteEndIdx]);

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    if (selectedDocIndex === null) {
      toast.error('Please select a document');
      return;
    }
    if (!selectedQuote.trim()) {
      toast.error('Please select a quote from the document');
      return;
    }

    const doc = activeDocuments[selectedDocIndex];

    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        simulation_token: simulationToken,
        documentation_search_event_id: documentationSearchEventId,
        message: message.trim(),
        quote: { exact_document_quotes: [selectedQuote.trim()] },
      };

      // Send documentation_retrieved_id if available (from the doc search), otherwise documentation_id
      if (doc.documentation_retrieved_id) {
        body.documentation_retrieved_id = doc.documentation_retrieved_id;
      } else if (doc.documentation_id) {
        body.documentation_id = doc.documentation_id;
      }

      const response = await fetch(`${BACKEND_URL}/vnog/create_human_ai_operator_message/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': Cookies.get('csrftoken') || '',
        },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const err = await response.json();
        toast.error(err.error || 'Failed to create operator message');
        setSubmitting(false);
        return;
      }

      const eventData = await response.json();
      onEventCreated(eventData);
      toast.success('Operator message created');
      handleClose();
    } catch {
      toast.error('Failed to create operator message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setMessage('');
    setDefaultDocuments([]);
    setSearchQuery('');
    setSearchResults([]);
    setShowingSearchResults(false);
    setSelectedDocIndex(null);
    setDocumentText('');
    resetQuoteState();
    onClose();
  };

  // Render the document text as clickable tokens with highlighting
  const renderDocumentText = () => {
    if (!documentText) return null;

    const tokens = tokenize(documentText);

    // Determine highlight ranges
    const quoteStart = quoteStartIdx !== null && quoteEndIdx !== null
      ? Math.min(quoteStartIdx, quoteEndIdx) : null;
    const quoteEnd = quoteStartIdx !== null && quoteEndIdx !== null
      ? Math.max(quoteStartIdx, quoteEndIdx) : null;

    const searchStart = textSearchMatchIdx >= 0 ? textSearchMatchIdx : null;
    const searchEnd = searchStart !== null ? searchStart + textSearch.length : null;

    return (
      <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono leading-relaxed select-none">
        {tokens.map((token, i) => {
          // Check if this token is within the selected quote range
          const inQuote = quoteStart !== null && quoteEnd !== null &&
            token.start < quoteEnd && token.end > quoteStart;

          // Check if this is the start marker (no end set yet)
          const isStartMarker = quoteStartIdx !== null && quoteEndIdx === null &&
            token.start <= quoteStartIdx && token.end > quoteStartIdx;

          // Check if this token is in the text search highlight
          const inSearch = searchStart !== null && searchEnd !== null &&
            token.start < searchEnd && token.end > searchStart;

          let className = '';
          if (inQuote) {
            className = 'bg-blue-200 border-b-2 border-blue-500';
          } else if (isStartMarker) {
            className = 'bg-green-200 border-b-2 border-green-500';
          } else if (inSearch) {
            className = 'bg-yellow-200';
          }

          const needsRef = (inSearch && !inQuote && !isStartMarker) || isStartMarker ||
            (inQuote && token.start === quoteStart);

          if (token.isWhitespace) {
            return (
              <span
                key={i}
                ref={needsRef ? highlightRef : undefined}
                className={className}
                onClick={() => handleTokenClick(token.start, token.end)}
                style={{ cursor: 'pointer' }}
              >
                {token.text}
              </span>
            );
          }

          return (
            <span
              key={i}
              ref={needsRef ? highlightRef : undefined}
              className={`${className} ${!inQuote ? 'hover:bg-gray-200' : ''} rounded-sm transition-colors`}
              onClick={() => handleTokenClick(token.start, token.end)}
              style={{ cursor: 'pointer' }}
            >
              {token.text}
            </span>
          );
        })}
      </pre>
    );
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={handleClose}>
      <div className="w-full max-w-4xl mx-auto max-h-[90vh] overflow-hidden flex flex-col">
        <div className="relative rounded-xl bg-white shadow-lg border border-gray-100 p-6 flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 flex-shrink-0">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Suggest Operator Message</h3>
              <p className="text-sm text-gray-500 mt-1">
                Create a human-suggested AI operator message for training purposes.
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
              aria-label="Close"
            >
              <IconX size={20} />
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto space-y-5 pr-1">
            {/* Section 1: Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                placeholder="What should the AI operator suggest here?"
                className="w-full resize-y rounded-lg border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none p-3 text-sm text-gray-800 placeholder-gray-400"
              />
            </div>

            {/* Section 2: Document Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Document
              </label>

              {/* Search bar for finding any document */}
              <div className="relative mb-3">
                <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search all documents by summary..."
                  className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults([]);
                      setShowingSearchResults(false);
                      setSelectedDocIndex(null);
                      setDocumentText('');
                      resetQuoteState();
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 text-gray-400"
                  >
                    <IconX size={14} />
                  </button>
                )}
              </div>

              {/* Toggle label showing which list is displayed */}
              {showingSearchResults && (
                <div className="flex items-center gap-2 mb-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowingSearchResults(false);
                      setSelectedDocIndex(null);
                      setDocumentText('');
                      resetQuoteState();
                    }}
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    <IconArrowLeft size={12} />
                    Back to default documents
                  </button>
                  <span className="text-xs text-gray-500">
                    Showing search results for &quot;{searchQuery}&quot;
                  </span>
                </div>
              )}

              {/* Document list */}
              {(showingSearchResults ? searchingDocs : loadingDocs) ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                  <div className="h-4 w-4 rounded-full border-2 border-gray-300 border-t-transparent animate-spin" />
                  {showingSearchResults ? 'Searching documents...' : 'Loading documents...'}
                </div>
              ) : activeDocuments.length === 0 ? (
                <p className="text-sm text-gray-500 py-2">
                  {showingSearchResults ? 'No documents match your search.' : 'No documents found for this search.'}
                </p>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {!showingSearchResults && (
                    <p className="text-xs text-gray-400 px-3 py-1">Documents from this documentation search:</p>
                  )}
                  {activeDocuments.map((doc, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setSelectedDocIndex(idx)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        selectedDocIndex === idx
                          ? 'bg-blue-50 ring-1 ring-blue-300 text-blue-900'
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <span className="font-medium">{doc.document_name || doc.document?.name || 'Untitled'}</span>
                      {doc.similarity_score != null && (
                        <span className="ml-2 text-xs text-gray-400">
                          Score: {(doc.similarity_score * 100).toFixed(1)}%
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Section 3: Document text & quote selection */}
              {selectedDocIndex !== null && (
                <div className="mt-4 space-y-3">
                  {/* Instructions + search within document */}
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Select Quote
                      </label>
                      <p className="text-xs text-gray-500 mb-2">
                        Use the search to find text, then click a word to set the <span className="text-green-700 font-medium">start</span> and click another to set the <span className="text-blue-700 font-medium">end</span> of your quote.
                      </p>
                    </div>
                    {(quoteStartIdx !== null || quoteEndIdx !== null) && (
                      <button
                        type="button"
                        onClick={resetQuoteState}
                        className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-600 hover:bg-gray-50 whitespace-nowrap mt-5"
                      >
                        Clear selection
                      </button>
                    )}
                  </div>

                  {/* Search within document text */}
                  <div className="relative">
                    <IconSearch size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={textSearch}
                      onChange={(e) => setTextSearch(e.target.value)}
                      placeholder="Search within document text..."
                      className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none text-sm"
                    />
                    {textSearch && textSearchMatchIdx === -1 && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-amber-600">
                        No match
                      </span>
                    )}
                    {textSearch && textSearchMatchIdx >= 0 && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-green-600">
                        Found
                      </span>
                    )}
                  </div>

                  {/* Selection status */}
                  <div className="flex items-center gap-3 text-xs">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${
                      quoteStartIdx !== null ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      ● Start: {quoteStartIdx !== null ? 'Set' : 'Click a word'}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full ${
                      quoteEndIdx !== null ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      ● End: {quoteEndIdx !== null ? 'Set' : quoteStartIdx !== null ? 'Click another word' : 'Waiting for start'}
                    </span>
                  </div>

                  {/* Document text display */}
                  <div>
                    {loadingText ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                        <div className="h-4 w-4 rounded-full border-2 border-gray-300 border-t-transparent animate-spin" />
                        Loading document text...
                      </div>
                    ) : (
                      <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                        {renderDocumentText()}
                      </div>
                    )}
                  </div>

                  {/* Selected quote preview */}
                  {selectedQuote && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs font-medium text-blue-700 mb-1">Selected quote:</p>
                      <p className="text-sm text-blue-900 italic whitespace-pre-wrap max-h-24 overflow-y-auto">
                        &quot;{selectedQuote}&quot;
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100 mt-4 flex-shrink-0">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 rounded-md text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={submitting || !message.trim() || selectedDocIndex === null || !selectedQuote.trim()}
              onClick={handleSubmit}
              className={`px-4 py-2 rounded-md text-white text-sm font-medium transition-colors ${
                submitting || !message.trim() || selectedDocIndex === null || !selectedQuote.trim()
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {submitting ? 'Creating...' : 'Create Operator Message'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default HumanOperatorMessageModal;
